import { ecsClient, getGlobalSessionId } from "./index";
import { UserBill, StartRefundBillResponse } from "../types/bill";
import { RefundProcess } from "../types/refundProcess";

const BillServices = {
  uploadBill: async (
    file:
      | {
          uri?: string;
          name: string;
          type: string;
        }
      | File,
    onProgress?: (_progress: number) => void
  ): Promise<UserBill | null> => {
    try {
      const formData = new FormData();
      let fileToUpload: File;

      // Check if it's already a File object (from native input)
      if (file instanceof File) {
        fileToUpload = file;
      } else if (file.uri) {
        // Try to handle expo-document-picker result
        // Check if the URI is a data URL or blob URL
        if (file.uri.startsWith("data:") || file.uri.startsWith("blob:")) {
          const response = await fetch(file.uri);
          const blob = await response.blob();
          fileToUpload = new File([blob], file.name, {
            type: file.type || "application/pdf",
          });
        } else {
          // Fallback - create empty file if fetch fails
          console.error("Unable to process file URI:", file.uri);
          throw new Error("Invalid file format for web upload");
        }
      } else {
        throw new Error("No valid file provided");
      }

      // Append the file
      formData.append("file", fileToUpload);

      // Add sessionId to FormData
      const sessionId = getGlobalSessionId();
      if (sessionId) {
        formData.append("sessionId", sessionId);
      }

      console.log("Uploading file:", {
        name: fileToUpload.name,
        size: fileToUpload.size,
        type: fileToUpload.type,
      });

      const uploadResponse = await ecsClient.post<UserBill>(
        "/bills/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          transformRequest: (_data, _headers) => {
            // This is the critical fix for axios FormData in React Native/Web
            return formData;
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(progress);
            }
          },
        }
      );

      return uploadResponse.data;
    } catch (error: any) {
      console.error("Bill upload error:", error);
      throw error;
    }
  },

  getBills: async (): Promise<UserBill[]> => {
    try {
      const sessionId = getGlobalSessionId();
      const response = await ecsClient.get<UserBill[]>("/bills", {
        params: { sessionId },
      });

      return response.data || [];
    } catch (error: any) {
      console.error("❌ Get bills error:", {
        error: error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      return [];
    }
  },

  startRefund: async (
    accountNumber: string,
    billingDate: string
  ): Promise<StartRefundBillResponse | null> => {
    try {
      const response = await ecsClient.post<StartRefundBillResponse>(
        "/refund/bill",
        {
          accountNumber,
          billingDate,
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Refund initiation error:", error);
      throw error;
    }
  },

  deleteBill: async (
    accountNumber: string,
    billingDate: string
  ): Promise<boolean> => {
    try {
      const sessionId = getGlobalSessionId();
      const response = await ecsClient.delete(
        `/bills/${accountNumber}/${billingDate}`,
        {
          params: { sessionId },
        }
      );

      return response.status === 200;
    } catch (error: any) {
      console.error("Delete bill error:", error);
      throw error;
    }
  },

  getBillRefundProcesses: async (
    accountNumber: string,
    billingDate: string
  ): Promise<RefundProcess[]> => {
    try {
      const sessionId = getGlobalSessionId();
      const response = await ecsClient.get<RefundProcess>(
        `/bills/${accountNumber}/${billingDate}/refund`,
        {
          params: { sessionId },
        }
      );

      // API returns a single RefundProcess object, wrap it in an array
      return response.data ? [response.data] : [];
    } catch (error: any) {
      console.error("Get bill refund processes error:", error);
      return [];
    }
  },
};

export default BillServices;
